import { useState } from "react";
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { PageHeader, Panel } from "@/components/layout/AppShell";
import { usePoll } from "@/context/AuthContext";
import { fmt } from "@/lib/format";

const SEGS = [["", "All"], ["option-buyer", "Option Buyer"], ["futures", "Futures"], ["intraday", "Intraday"], ["commodity", "Commodity"]];
const statusTone = { "TARGET HIT": "text-[#00FF66]", "SL HIT": "text-[#FF3366]", OPEN: "text-[#007AFF]", EXPIRED: "text-gray-400" };

const Stat = ({ label, value, tone, testId }) => (
  <div className="rx-card p-4" data-testid={testId}>
    <div className="rx-label">{label}</div>
    <div className={`font-data text-2xl font-bold mt-2 ${tone || ""}`}>{value}</div>
  </div>
);

export default function PerformancePage() {
  const [seg, setSeg] = useState("");
  const { data: perf } = usePoll("/performance", 15000);
  const { data: hist } = usePoll(`/signals/history?limit=100${seg ? `&segment=${seg}` : ""}`, 6000, [seg]);
  return (
    <div data-testid="performance-page">
      <PageHeader eyebrow="Track record" title="Signal History & Performance" sub="Every signal is logged server-side and auto-closed on Target, Stop Loss or 45-minute expiry. Feed is simulated." />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Stat label="Closed signals" value={perf?.total ?? "—"} testId="stat-total" />
        <Stat label="Win rate" value={perf ? `${perf.win_rate}%` : "—"} tone="text-[#00FF66]" testId="stat-winrate" />
        <Stat label="Net return (sum %)" value={perf ? `${perf.net_pct}%` : "—"} tone={perf?.net_pct >= 0 ? "text-[#00FF66]" : "text-[#FF3366]"} testId="stat-net" />
        <Stat label="Open now" value={perf?.open ?? "—"} tone="text-[#007AFF]" testId="stat-open" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6 mb-6">
        <Panel className="lg:col-span-2" title="Cumulative equity (%)" testId="equity-panel">
          <div className="h-64">
            <ResponsiveContainer>
              <AreaChart data={perf?.equity || []}>
                <defs><linearGradient id="eq" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#00FF66" stopOpacity={0.35} /><stop offset="100%" stopColor="#00FF66" stopOpacity={0} /></linearGradient></defs>
                <XAxis dataKey="t" hide /><YAxis stroke="#4A5568" fontSize={11} width={40} />
                <Tooltip contentStyle={{ background: "#11151C", border: "1px solid rgba(255,255,255,.1)", fontFamily: "JetBrains Mono", fontSize: 12 }} labelFormatter={(l) => new Date(l).toLocaleString("en-IN")} />
                <Area type="monotone" dataKey="v" stroke="#00FF66" fill="url(#eq)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Panel>
        <Panel title="By segment" testId="segment-performance">
          <div className="space-y-3">
            {(perf?.by_segment || []).map((s) => (
              <div key={s.segment} className="p-3 rounded-lg bg-white/[0.02] border border-white/5">
                <div className="flex justify-between text-sm"><span className="font-semibold capitalize">{s.segment.replace("-", " ")}</span><span className="font-data text-[#00FF66]">{s.win_rate}%</span></div>
                <div className="flex justify-between text-xs text-gray-500 font-data mt-1"><span>{s.wins}/{s.total} wins</span><span>net {s.net_pct}%</span></div>
              </div>
            ))}
          </div>
        </Panel>
      </div>
      <Panel title="Signal log" testId="history-panel" right={
        <div className="flex flex-wrap gap-1">{SEGS.map(([k, l]) => (
          <button key={k || "all"} onClick={() => setSeg(k)} data-testid={`history-filter-${k || "all"}`}
            className={`px-3 py-1 rounded-full text-xs transition-colors ${seg === k ? "bg-[#171C24] text-[#00FF66]" : "text-gray-400 hover:text-white"}`}>{l}</button>
        ))}</div>}>
        <div className="overflow-x-auto rx-scroll">
          <table className="w-full text-sm min-w-[820px]" data-testid="history-table">
            <thead><tr className="text-left text-[10px] uppercase tracking-wider text-gray-500 border-b border-white/5">
              {["Time", "Segment", "Instrument", "Side", "Score", "Entry", "SL", "Target", "Exit", "P&L %", "Status"].map((h) => <th key={h} className="py-2 px-2">{h}</th>)}
            </tr></thead>
            <tbody className="font-data">
              {(hist?.items || []).map((r) => (
                <tr key={r.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                  <td className="py-2 px-2 text-xs text-gray-400">{new Date(r.opened_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</td>
                  <td className="px-2 text-xs capitalize text-gray-400">{r.segment.replace("-", " ")}</td>
                  <td className="px-2">{r.instrument}</td>
                  <td className={`px-2 ${r.direction === "LONG" ? "text-[#00FF66]" : "text-[#FF3366]"}`}>{r.direction}</td>
                  <td className="px-2">{r.score}</td><td className="px-2">{fmt(r.entry)}</td><td className="px-2">{fmt(r.sl)}</td>
                  <td className="px-2">{fmt(r.target)}</td><td className="px-2">{r.exit ? fmt(r.exit) : "—"}</td>
                  <td className={`px-2 ${(r.pnl_pct ?? 0) >= 0 ? "text-[#00FF66]" : "text-[#FF3366]"}`}>{r.pnl_pct != null ? `${r.pnl_pct}%` : "—"}</td>
                  <td className={`px-2 text-xs font-bold ${statusTone[r.status]}`}>{r.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}
